import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import {
  ActionCard,
  AlertDialog,
  AppText,
  Chip,
  IconBox,
} from '@zippeditoolsjs/ui-web';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';

export default function MfaEnrollmentChooser({
  onPickTotp,
  onPickSms,
  onLater,
  onDontAskAgain,
  canDismiss,
  dontAskMonths,
  busy = false,
}) {
  const { t } = useTranslation();
  const [dontAskDialogOpen, setDontAskDialogOpen] = useState(false);

  const confirmDontAsk = () => {
    setDontAskDialogOpen(false);
    onDontAskAgain();
  };

  return (
    <div className="flex flex-col">
      <AppText variant="titleXLarge" as="h3" className="mb-2">
        {t('heimdal_app.TOTP.enroll_choose_title')}
      </AppText>
      <AppText variant="bodyMedium" as="p" className="mb-6 text-secondary">
        {t('heimdal_app.TOTP.enroll_choose_subtitle')}
      </AppText>

      <div className="flex flex-col gap-2.5">
        <ActionCard
          startAdornment={
            <IconBox size="sm" tone="accent">
              <IconComponent iconName="shield-checkmark" style={{ fontSize: 18 }} />
            </IconBox>
          }
          title={t('heimdal_app.TOTP.authenticator_app')}
          hint={t('heimdal_app.TOTP.method_totp_description')}
          titleAdornment={
            <Chip tone="success">{t('heimdal_app.TOTP.recommended')}</Chip>
          }
          disabled={busy}
          onClick={onPickTotp}
        />
        <ActionCard
          startAdornment={
            <IconBox size="sm" tone="muted">
              <IconComponent
                iconName="chatbubble-ellipses-outline"
                style={{ fontSize: 18 }}
              />
            </IconBox>
          }
          title={t('heimdal_app.TOTP.enroll_sms_title')}
          hint={t('heimdal_app.TOTP.method_sms_description')}
          disabled={busy}
          onClick={onPickSms}
        />
        {onLater ? (
          <ActionCard
            startAdornment={
              <IconBox size="sm" tone="muted">
                <IconComponent iconName="time-outline" style={{ fontSize: 18 }} />
              </IconBox>
            }
            title={t('heimdal_app.TOTP.offer_later')}
            hint={t('heimdal_app.TOTP.offer_later_hint')}
            disabled={busy}
            onClick={onLater}
          />
        ) : null}
      </div>

      {canDismiss ? (
        <div className="mt-6 flex flex-col gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={() => setDontAskDialogOpen(true)}
            className="cursor-pointer self-start border-0 bg-transparent p-0 font-sans text-xs font-medium text-muted hover:underline disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:no-underline">
            {t('heimdal_app.TOTP.offer_dont_ask')}
          </button>
          <AlertDialog
            open={dontAskDialogOpen}
            onOpenChange={setDontAskDialogOpen}
            title={t('heimdal_app.TOTP.offer_dont_ask')}
            description={t('heimdal_app.TOTP.offer_dont_ask_notice', {
              months: dontAskMonths,
            })}
            confirmLabel={t('heimdal_app.TOTP.offer_dont_ask_confirm')}
            cancelLabel={t('heimdal_app.TOTP.cancel')}
            onConfirm={confirmDontAsk}
            onCancel={() => setDontAskDialogOpen(false)}
          />
        </div>
      ) : null}
    </div>
  );
}

MfaEnrollmentChooser.propTypes = {
  onPickTotp: PropTypes.func.isRequired,
  onPickSms: PropTypes.func.isRequired,
  onLater: PropTypes.func,
  onDontAskAgain: PropTypes.func,
  canDismiss: PropTypes.bool,
  dontAskMonths: PropTypes.number,
  busy: PropTypes.bool,
};
